import * as React from 'react';
import { graphql } from 'react-apollo';

import { branch, compose, withProps, withState } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import User from 'js/lib/user';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import { enterpriseContractType } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import { PROGRAM_VISIBILITY_RULE } from 'bundles/enterprise-legacy-learner-home/constants/ProgramCommonConstants';
import {
  C4C_CORONA_TRIAL,
  C4C_FREEMIUM,
  C4C_UPSWELL,
  C4WR_CORONA_TRIAL,
  COURSERA_CORE,
  GWG_FREEMIUM,
} from 'bundles/enterprise-legacy-learner-home/constants/ProgramTypes';
import type {
  EnterpriseProgramBySlugQueryResult,
  EnterpriseProgramBySlugQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/EnterpriseProgramBySlugQuery';
import EnterpriseProgramBySlugQuery from 'bundles/enterprise-legacy-learner-home/queries/EnterpriseProgramBySlugQuery';
import FindUserEmailsByUserIdQuery from 'bundles/enterprise-legacy-learner-home/queries/FindUserEmailsByUserIdQuery';
import type {
  FindUserEmailsByUserIdQueryResult,
  FindUserEmailsByUserIdQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/queries/FindUserEmailsByUserIdQuery';
import type { EnterpriseContractTagsV1 as EnterpriseContractTagsV1Type } from 'bundles/naptimejs/resources/__generated__/EnterpriseContractTagsV1';
import type { EnterpriseContractsV1 as EnterpriseContractsV1Type } from 'bundles/naptimejs/resources/__generated__/EnterpriseContractsV1';
import type { EnterpriseProgramsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseProgramsV1';
import type { ProgramBrowsingExperiencesV1 as ProgramBrowsingExperiencesV1Type } from 'bundles/naptimejs/resources/__generated__/ProgramBrowsingExperiencesV1';
import type { ThirdPartyOrganizationsV1 as ThirdPartyOrganizationsV1Type } from 'bundles/naptimejs/resources/__generated__/ThirdPartyOrganizationsV1';
import EnterpriseNoticeAcceptanceLogsV1 from 'bundles/naptimejs/resources/enterpriseNoticeAcceptanceLogs.v1';
import ProgramBrowsingExperienceV1 from 'bundles/naptimejs/resources/programBrowsingExperiences.v1';
import ProgramMembershipsV2 from 'bundles/naptimejs/resources/programMemberships.v2';
import ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';
import ProgramHomeLoggedInQueryText from 'bundles/program-home/queries/ProgramHomeLoggedInQuery';
import type {
  ProgramHomeLoggedInQuery,
  ProgramHomeLoggedInQueryVariables,
} from 'bundles/program-home/queries/ProgramHomeLoggedInQuery';
import EnterpriseProgramContext from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import ThirdPartyOrganizationContext from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import type { PropsFromWithEnterpriseAdminRoles } from 'bundles/unified-common/hocs/withEnterpriseAdminRoles';
import withEnterpriseAdminRoles from 'bundles/unified-common/hocs/withEnterpriseAdminRoles';
import type { PropsFromWithEnterpriseProgramMembership } from 'bundles/unified-common/hocs/withEnterpriseProgramMembership';
import withEnterpriseProgramMembership from 'bundles/unified-common/hocs/withEnterpriseProgramMembership';
import { usePermittedProducts } from 'bundles/unified-common/hooks/usePermittedProducts';

const WILDCARD_BROWSING_EXPERIENCE = 'DEEP_BROWSE';

type PropsFromWithProps = {
  userId: string;
  isAuthenticatedUser: boolean;
  isSuperuser: boolean;
};

type PropsFromWithEnterpriseProgramDataQuery = {
  programs?: EnterpriseProgramsV1[] | null;
  thirdPartyOrg?: ThirdPartyOrganizationsV1Type | null;
  programBrowsingExperience?: ProgramBrowsingExperiencesV1Type | null;
  programContractTags?: EnterpriseContractTagsV1Type[] | null;
  enterpriseContracts?: EnterpriseContractsV1Type[] | null;
  renderErrorPage: boolean;
};

type PropsFromCAWithProps = {
  isCareerAcademyV2: boolean;
};

type PropsFromWithEnterpriseProgramDataWithProps = {
  program: EnterpriseProgramsV1;
  programId: string;
  programName: string;
  thirdPartyOrganizationId: string;
  requirePreJoinCheck: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
  thirdPartyOrganization: ThirdPartyOrganizationsV1;
  orgName: string;
  isC4cv: boolean;
  isUpswell: boolean;
  isC4er: boolean;
  isC4wr: boolean;
  isC4C: boolean;
  isGwGProgram: boolean;
  isWESContract: boolean;
  showNoCertificateNotification: boolean;
  isC4Program: boolean;
};

type PropsFromWithEnterpriseProgramData = PropsFromWithEnterpriseProgramDataQuery &
  PropsFromWithEnterpriseProgramDataWithProps &
  PropsFromCAWithProps;

type PropsForProgramHomeLoggedInQuery = Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'programId' | 'thirdPartyOrganizationId' | 'requirePreJoinCheck'> &
  Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'requirePreJoinCheck'>;

type PropsFromProgramHomeLoggedInQuery = {
  renderErrorPage: boolean;
  programMembership?: ProgramMembershipsV2;
  acceptanceNoticeLogs?: EnterpriseNoticeAcceptanceLogsV1[];
  reloadProgramMembership?: () => void;
  isProgramMember: boolean;
  isInvited: boolean;
};

type PropsForDeferredProgramHomeLoggedInQuery = Pick<PropsFromWithProps, 'userId'> &
  Pick<PropsFromWithEnterpriseProgramData, 'programId'>;

type PropsFromDeferredProgramHomeLoggedInQuery = {
  renderErrorPage: boolean;
  isProgramAdmin: boolean;
};

type PropsForWithLoggedInData = PropsForProgramHomeLoggedInQuery & PropsForDeferredProgramHomeLoggedInQuery;

type PropsFromWithLoggedInData = Required<Omit<PropsFromProgramHomeLoggedInQuery, 'renderErrorPage'>> &
  Required<Omit<PropsFromDeferredProgramHomeLoggedInQuery, 'renderErrorPage'>>;

type PropsFromCaller = {
  children: React.ReactNode;
};

type PropsFromRouter = {
  programSlug: string;
};

type PropsFromProgram = {
  program?: EnterpriseProgramsV1;
  thirdPartyOrganization?: ThirdPartyOrganizationsV1Type;
  isProgramLoading?: boolean;
  isC4cv?: boolean;
  isUpswell?: boolean;
  isC4C?: boolean;
  hasFreemiumTag?: boolean;
  isGwGProgram?: boolean;
  showNoCertificateNotification?: boolean;
  isWildcard?: boolean;
  requirePreJoinCheck?: boolean;
  browsingExperienceType?: ProgramBrowsingExperienceV1;
};

type PropsFromFindUserEmailsByUserIdQuery = {
  userEmails?: UserEmail[];
  refetchUserEmails?: () => Promise<unknown> | unknown;
};

type PropsToComponent = PropsFromCaller &
  PropsFromProgram &
  PropsFromWithEnterpriseProgramMembership &
  PropsFromWithEnterpriseAdminRoles &
  PropsFromFindUserEmailsByUserIdQuery &
  PropsFromWithLoggedInData & {
    programBrowsingExperience?: ProgramBrowsingExperiencesV1Type;
    programContractTags?: EnterpriseContractTagsV1Type[];
    enterpriseContracts?: EnterpriseContractsV1Type[];
    isC4er: boolean;
    isC4wr: boolean;
    isWESContract: boolean;
    isC4Program: boolean;
  };

type PropsForWithLoggedInDataBranch = {
  userId?: string;
  program?: EnterpriseProgramsV1;
  isProgramLoading?: boolean;
};

const EnterpriseDataProvider = ({
  children,
  program,
  isProgramLoading,
  thirdPartyOrganization,
  isC4cv,
  isC4C,
  isUpswell,
  programMembership,
  isProgramMember,
  membershipCreatedAt,
  isMembershipLoading,
  isProgramAdmin,
  isInvited,
  userEmails,
  refetchUserEmails,
  hasFreemiumTag,
  isGwGProgram,
  showNoCertificateNotification,
  isWildcard,
  acceptanceNoticeLogs,
  requirePreJoinCheck,
  browsingExperienceType,
  programBrowsingExperience,
  programContractTags,
  enterpriseContracts,
  isC4er,
  isC4wr,
  isWESContract,
  isC4Program,
}: PropsToComponent) => {
  const { permittedProducts } = usePermittedProducts(program?.id);
  const isLimitedContent = permittedProducts?.accessType === 'WHITELIST';
  return (
    <EnterpriseProgramContext.Provider
      value={{
        program,
        isProgramLoading,
        isC4cv,
        isC4C,
        isUpswell,
        programMembership,
        isProgramMember,
        isProgramAdmin,
        isInvited,
        userEmails,
        hasFreemiumTag,
        isGwGProgram,
        showNoCertificateNotification,
        membershipCreatedAt,
        isMembershipLoading,
        isWildcard,
        refetchUserEmails,
        acceptanceNoticeLogs,
        requirePreJoinCheck,
        browsingExperienceType,
        programBrowsingExperience,
        programContractTags,
        enterpriseContracts,
        isC4er,
        isC4wr,
        isWESContract,
        isC4Program,
        isLimitedContent,
      }}
    >
      <ThirdPartyOrganizationContext.Provider value={thirdPartyOrganization}>
        {children}
      </ThirdPartyOrganizationContext.Provider>
    </EnterpriseProgramContext.Provider>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(
  connectToRouter((router) => {
    return {
      programSlug: router.params.programSlug || router.location.query?.programSlug,
    };
  }),
  graphql<PropsFromRouter, EnterpriseProgramBySlugQueryResult, EnterpriseProgramBySlugQueryVariables, PropsFromProgram>(
    EnterpriseProgramBySlugQuery,
    {
      options: ({ programSlug }) => ({ variables: { programSlug } }),
      props: ({ data }) => {
        const programs = data?.EnterpriseProgramsV1?.slug?.elements;
        const programContractTags = data?.EnterpriseProgramsV1?.slug?.linked?.enterpriseContractTagsV1;
        const programBrowsingExperience = data?.EnterpriseProgramsV1?.slug?.linked?.programBrowsingExperiencesV1?.[0];
        const enterpriseContracts = data?.EnterpriseProgramsV1?.slug?.linked?.enterpriseContractsV1;
        const contractType = enterpriseContracts?.[0]?.contractType;
        const isWESContract = contractType === enterpriseContractType.SELF_SERVE;
        const thirdPartyOrganization = data?.EnterpriseProgramsV1?.slug?.linked?.thirdPartyOrganizationsV1?.[0];

        let thirdPartyOrg = null;
        if (thirdPartyOrganization) {
          thirdPartyOrg = new ThirdPartyOrganizationsV1(thirdPartyOrganization);
        }
        const requirePreJoinCheck = thirdPartyOrg
          ? thirdPartyOrg.programVisibilityRule === PROGRAM_VISIBILITY_RULE.ACCEPTED_PRIVACY_NOTICE
          : false;

        const browsingExperienceType = programBrowsingExperience
          ? new ProgramBrowsingExperienceV1(programBrowsingExperience)
          : undefined;

        const hasFreemiumTag =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === C4C_FREEMIUM) ?? false;
        const isC4cv =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === C4C_CORONA_TRIAL) ||
          hasFreemiumTag;
        const isUpswell =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === C4C_UPSWELL) ?? false;
        const isC4C =
          programContractTags?.some((programContractTag) => programContractTag.contractTag.startsWith('C4C')) ?? false;
        const isC4wr =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === C4WR_CORONA_TRIAL) ??
          false;
        const isGwGProgram =
          programContractTags?.some((programContractTag) => GWG_FREEMIUM.includes(programContractTag.contractTag)) ??
          false;
        const showNoCertificateNotification = isUpswell || isC4cv || isC4wr || hasFreemiumTag;
        const isWildcard =
          data?.EnterpriseProgramsV1?.slug?.linked?.programBrowsingExperiencesV1?.[0].browsingExperience ===
          WILDCARD_BROWSING_EXPERIENCE;
        const isC4er =
          programContractTags?.some((programContractTag) => programContractTag.contractTag === COURSERA_CORE) ?? false;
        const isC4Program = isC4cv || isC4er || isC4wr || isUpswell;

        return {
          programBrowsingExperience,
          programContractTags,
          enterpriseContracts,
          isC4er,
          isC4wr,
          isWESContract,
          isC4Program,
          requirePreJoinCheck,
          browsingExperienceType,
          program: programs ? programs[0] : undefined,
          thirdPartyOrganization,
          thirdPartyOrganizationId: thirdPartyOrganization?.id,
          isProgramLoading: data?.loading,
          isC4cv,
          isUpswell,
          isC4C,
          hasFreemiumTag,
          isGwGProgram,
          showNoCertificateNotification,
          isWildcard,
          programId: programs ? programs[0].id : undefined,
        };
      },
    }
  ),
  withProps<PropsFromWithProps, {}>(() => ({
    userId: User.get().id?.toString(),
    isAuthenticatedUser: User.isAuthenticatedUser(),
    isSuperuser: User.isSuperuser(),
  })),
  branch<PropsForWithLoggedInDataBranch>(
    (props) => Boolean(props.userId) && !props.program?.metadata.endedAt && !props.isProgramLoading,
    compose<PropsForWithLoggedInData, PropsFromWithLoggedInData>(
      graphql<
        PropsForProgramHomeLoggedInQuery,
        ProgramHomeLoggedInQuery,
        ProgramHomeLoggedInQueryVariables,
        PropsFromProgramHomeLoggedInQuery
      >(ProgramHomeLoggedInQueryText, {
        options: ({ programId, userId, thirdPartyOrganizationId, requirePreJoinCheck }) => ({
          variables: {
            programMembershipId: `${userId}~${programId}`,
            userId: userId.toString(),
            thirdPartyOrganizationId,
            requirePreJoinCheck,
          },
        }),
        props: ({ data, ownProps: { requirePreJoinCheck } }): PropsFromProgramHomeLoggedInQuery => {
          const programMembershipsV2 = data?.ProgramMembershipsV2?.get?.elements?.[0];
          const programMembership = programMembershipsV2 && new ProgramMembershipsV2(programMembershipsV2);
          const enterpriseNoticeAcceptanceLogsV1 = requirePreJoinCheck
            ? data?.EnterpriseNoticeAcceptanceLogsV1?.getByUserIdAndOrganizationId?.elements
            : undefined;
          const acceptanceNoticeLogs = enterpriseNoticeAcceptanceLogsV1?.map(
            (x) => new EnterpriseNoticeAcceptanceLogsV1(x)
          );
          const reloadProgramMembership = data?.refetch;
          const isProgramMember = Boolean(programMembership?.isMember);
          const isInvited = Boolean(programMembership?.isInvited);

          return {
            renderErrorPage: Boolean(data?.error),
            programMembership,
            acceptanceNoticeLogs,
            reloadProgramMembership,
            isProgramMember,
            isInvited,
          };
        },
      }),
      graphql<
        {},
        FindUserEmailsByUserIdQueryResult,
        FindUserEmailsByUserIdQueryVariables,
        PropsFromFindUserEmailsByUserIdQuery
      >(FindUserEmailsByUserIdQuery, {
        props: ({ data }) => {
          const userEmails = data?.FindUserEmailsByUserIdResponse?.userEmails;
          const refetchUserEmails = data?.refetch;
          return { userEmails, refetchUserEmails };
        },
        options: () => ({
          variables: {
            userId: `${User.get().id}`,
          },
          errorPolicy: 'all',
          ssr: false,
        }),
      }),
      withEnterpriseAdminRoles
    )
  ),
  withState('showFreemiumCourseReenrollModal', 'setShowFreemiumCourseReenrollModal', true),
  withEnterpriseProgramMembership
)(EnterpriseDataProvider);
